/**
 * Planned Works Dashboard
 * Main dashboard for planning and managing work activities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Stop as CompleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

import { useSiteContext } from '../../hooks/useSiteContext';
import plannedWorksService from '../../services/plannedWorksService';
import WorkCreationWizard from '../../components/PlannedWorks/WorkCreationWizard';
import WorkPackageManager from '../../components/PlannedWorks/WorkPackageManager';
import {
  PlannedWorksDashboard as PlannedWorksDashboardType,
  PlannedWorkListItem,
  WorkPackage,
  PlannedWorksFilters,
  getWorkStatusColor,
  getPriorityColor,
  WORK_STATUSES,
  WORK_PRIORITIES
} from '../../types/plannedWorks';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`planned-works-tabpanel-${index}`}
      aria-labelledby={`planned-works-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const PlannedWorksDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState<PlannedWorksDashboardType | null>(null);
  const [plannedWorks, setPlannedWorks] = useState<PlannedWorkListItem[]>([]);
  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [overdueWorks, setOverdueWorks] = useState<PlannedWorkListItem[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<PlannedWorkListItem[]>([]);
  
  // Filters and pagination
  const [filters, setFilters] = useState<PlannedWorksFilters>({});
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<WorkPackage | null>(null);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!currentSite || !currentSite.siteId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const dashboardData = await plannedWorksService.dashboard.getDashboard(currentSite.siteId);
      setDashboard(dashboardData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [currentSite]);

  // Handlers
  const handleWorkCreated = (work: any) => {
    // Refresh data
    fetchDashboardData();
    fetchPlannedWorks();
  };

  const handlePackageCreated = (pkg: WorkPackage) => {
    // Refresh data
    fetchDashboardData();
    fetchPlannedWorks();
    setSelectedPackage(null);
  };

  const openCreateWorkDialog = () => {
    setCreateDialogOpen(true);
  };

  const openCreatePackageDialog = () => {
    setSelectedPackage(null);
    setPackageDialogOpen(true);
  };

  const openEditPackageDialog = (pkg: WorkPackage) => {
    setSelectedPackage(pkg);
    setPackageDialogOpen(true);
  };

  // Fetch planned works
  const fetchPlannedWorks = useCallback(async () => {
    if (!currentSite || !currentSite.siteId) return;
    
    try {
      const response = await plannedWorksService.plannedWorks.getAll({
        ...filters,
        site: currentSite.siteId!,
        page,
        page_size: 20
      });
      
      setPlannedWorks(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error('Failed to fetch planned works:', err);
      setError('Failed to load planned works');
    }
  }, [currentSite, filters, page]);

  // Fetch work packages
  const fetchWorkPackages = useCallback(async () => {
    if (!currentSite) return;
    
    try {
      const response = await plannedWorksService.workPackages.getAll({
        site: currentSite.siteId!,
        page_size: 20
      });
      
      setWorkPackages(response.results);
    } catch (err) {
      console.error('Failed to fetch work packages:', err);
    }
  }, [currentSite]);

  // Fetch overdue and pending approvals
  const fetchSpecialLists = useCallback(async () => {
    if (!currentSite || !currentSite.siteId) return;
    
    try {
      const [overdue, pending] = await Promise.all([
        plannedWorksService.plannedWorks.getOverdue(),
        plannedWorksService.plannedWorks.getPendingApproval()
      ]);
      
      setOverdueWorks(overdue.filter(work => work.site === currentSite.siteId));
      setPendingApprovals(pending.filter(work => work.site === currentSite.siteId));
    } catch (err) {
      console.error('Failed to fetch special lists:', err);
    }
  }, [currentSite]);

  // Effects
  useEffect(() => {
    if (currentSite) {
      fetchDashboardData();
      fetchSpecialLists();
    }
  }, [currentSite, fetchDashboardData, fetchSpecialLists]);

  useEffect(() => {
    if (currentSite) {
      if (tabValue === 0) fetchPlannedWorks();
      else if (tabValue === 2) fetchWorkPackages();
    }
  }, [currentSite, tabValue, fetchPlannedWorks, fetchWorkPackages]);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    fetchDashboardData();
    fetchSpecialLists();
    if (tabValue === 0) fetchPlannedWorks();
    else if (tabValue === 2) fetchWorkPackages();
  };

  const handleFilterChange = (newFilters: PlannedWorksFilters) => {
    setFilters(newFilters);
    setPage(1);
    setFilterDialogOpen(false);
  };

  const handleWorkAction = async (workId: number, action: string) => {
    try {
      switch (action) {
        case 'start':
          await plannedWorksService.plannedWorks.startExecution(workId);
          break;
        case 'complete':
          // This would open a completion dialog
          break;
        default:
          break;
      }
      
      // Refresh data after action
      handleRefresh();
    } catch (err) {
      console.error(`Failed to ${action} work:`, err);
      setError(`Failed to ${action} work`);
    }
  };

  if (!currentSite) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a site to view planned works.</Alert>
      </Box>
    );
  }

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
                      Planned Works - {currentSite.siteName}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<GroupIcon />}
            onClick={openCreatePackageDialog}
          >
            Create Package
          </Button>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateWorkDialog}
          >
            Plan New Work
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {dashboard && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h4">{dashboard.total_works}</Typography>
                    <Typography color="textSecondary">Total Works</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge badgeContent={dashboard.overdue_works} color="error">
                    <WarningIcon sx={{ mr: 2, color: 'warning.main' }} />
                  </Badge>
                  <Box>
                    <Typography variant="h4">{dashboard.overdue_works}</Typography>
                    <Typography color="textSecondary">Overdue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Badge badgeContent={dashboard.pending_approvals} color="warning">
                    <AssignmentIcon sx={{ mr: 2, color: 'info.main' }} />
                  </Badge>
                  <Box>
                    <Typography variant="h4">{dashboard.pending_approvals}</Typography>
                    <Typography color="textSecondary">Pending Approval</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ mr: 2, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h4">{dashboard.active_packages}</Typography>
                    <Typography color="textSecondary">Active Packages</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="All Works" />
            <Tab label="Overdue" />
            <Tab label="Work Packages" />
            <Tab label="Pending Approvals" />
          </Tabs>
        </Box>

        {/* All Works Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Planned Works</Typography>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setFilterDialogOpen(true)}
            >
              Filter
            </Button>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Work Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plannedWorks.map((work) => (
                  <TableRow key={work.id}>
                    <TableCell>{work.work_number}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {work.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {work.work_type_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{work.work_type_category}</TableCell>
                    <TableCell>
                      <Chip
                        label={work.status_display}
                        color={getWorkStatusColor(work.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={work.priority_display}
                        color={getPriorityColor(work.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(work.proposed_start_date), 'MMM dd, yyyy')}
                      {work.is_overdue && (
                        <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{work.assigned_to_name || 'Unassigned'}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => navigate(`/planned-works/${work.id}`)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {work.status === 'ready_to_execute' && (
                        <Tooltip title="Start Work">
                          <IconButton 
                            size="small" 
                            onClick={() => handleWorkAction(work.id, 'start')}
                          >
                            <StartIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {work.status === 'in_progress' && (
                        <Tooltip title="Complete Work">
                          <IconButton 
                            size="small" 
                            onClick={() => handleWorkAction(work.id, 'complete')}
                          >
                            <CompleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Overdue Works Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Overdue Works ({overdueWorks.length})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Work Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Days Overdue</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overdueWorks.map((work) => (
                  <TableRow key={work.id}>
                    <TableCell>{work.work_number}</TableCell>
                    <TableCell>{work.title}</TableCell>
                    <TableCell>
                      {format(new Date(work.proposed_end_date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${Math.abs(work.days_until_proposed_start || 0)} days`}
                        color="error" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={work.status_display}
                        color={getWorkStatusColor(work.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => navigate(`/planned-works/${work.id}`)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Work Packages Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Work Packages</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {/* Open create package dialog */}}
            >
              Create Package
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            {workPackages.map((pkg) => (
              <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {pkg.package_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {pkg.package_description}
                    </Typography>
                    
                    <Box sx={{ my: 2 }}>
                      <Chip
                        label={pkg.status_display}
                        color={getWorkStatusColor(pkg.status)}
                        size="small"
                      />
                      <Chip
                        label={`${pkg.planned_works.length} works`}
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="body2">
                      <strong>Coordinator:</strong> {pkg.package_coordinator_name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Scheduled:</strong> {format(new Date(pkg.scheduled_start_date), 'MMM dd')} - {format(new Date(pkg.scheduled_end_date), 'MMM dd')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Progress:</strong> {pkg.completion_percentage}%
                    </Typography>
                    
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <Button size="small" startIcon={<ViewIcon />}>
                        View
                      </Button>
                      <Button size="small" startIcon={<EditIcon />}>
                        Edit
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Pending Approvals Tab */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Pending Approvals ({pendingApprovals.length})
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Work Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Current Stage</TableCell>
                  <TableCell>Proposed By</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingApprovals.map((work) => (
                  <TableRow key={work.id}>
                    <TableCell>{work.work_number}</TableCell>
                    <TableCell>{work.title}</TableCell>
                    <TableCell>
                      {work.current_approval_stage?.replace('_', ' ')}
                    </TableCell>
                    <TableCell>{work.proposed_by_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={work.priority_display}
                        color={getPriorityColor(work.priority)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined">
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Work Creation Wizard */}
      <WorkCreationWizard
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onWorkCreated={handleWorkCreated}
      />

      {/* Work Package Manager */}
      <WorkPackageManager
        open={packageDialogOpen}
        onClose={() => setPackageDialogOpen(false)}
        onPackageCreated={handlePackageCreated}
        existingPackage={selectedPackage || undefined}
        availableWorks={plannedWorks.filter(w => w.status === 'approved' || w.status === 'scheduled')}
      />
    </Box>
  );
};
